import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AlertComponent,
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  DialogService,
  EmptyComponent,
  InputComponent,
  SelectComponent,
  SkeletonComponent,
  SearchComponent,
  TableComponent,
  type ActionEvent,
  type SortChangeEvent,
  type TableConfig,
} from 'nebula-ui-kit';
import { finalize, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AdminTenantsService } from '../../../../data-access/src/lib/admin-users/admin-tenants.service';
import { AdminUsersService } from '../../../../data-access/src/lib/admin-users/admin-users.service';
import { AdminUserTenantsService } from '../../../../data-access/src/lib/admin-users/admin-user-tenants.service';
import { isUserTenantsEndpointMissing } from '../../../../data-access/src/lib/admin-users/admin-user-tenants.util';
import {
  inferAdminUserTenantsFromCatalog,
  mergeAdminUserTenants,
  hydrateAdminUserTenantsFromPersistedPrimary,
  persistAdminUserAssignmentSnapshot,
  resolvePrimaryAdminUserTenant,
  syncAdminUserAssignmentSnapshotsFromUsers,
  coalesceAdminUserSingleTenant,
  readAdminUserPrimaryTenant,
  userNeedsTenantEnrichment,
  userTenantAssignmentsToAdminTenants,
} from '../../../../data-access/src/lib/admin-users/admin-user-normalize.util';
import { DashboardService } from '../../../../data-access/src/lib/dashboard/dashboard.service';
import type {
  AdminSystemUser,
  AdminTenantSummary,
  AdminUserTenantInfo,
  AdminUserUpdatePayload,
} from '../../../../domain/src/lib/admin-users/admin-user.model';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  openVaultConfirmDialog,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import {
  canActorEditUserTenantAssignment,
  canDeleteAdminUser,
  ensureCanonicalSuperadminInAdminUserList,
  formatAdminUserCompanySummary,
  formatAdminUserRoleSummary,
  formatAdminUserRolesSummary,
  filterAssignableAdminTenants,
  resolveAssignableAdminUserTenantId,
  resolveAdminUserCreateConflictMessage,
  resolveAdminUserFormApiError,
  resolveAdminUserDeleteApiError,
  shouldAssignAdminUserFormEmailError,
  buildAdminUserCreatedSuccessFeedback,
  isAdminUserCreateProvisionComplete,
  resolveAdminUserCreateAccessUsername,
  buildAdminUserUpdatedSuccessFeedback,
  buildAdminUserToggleSuccessFeedback,
  buildAdminUserDeletedSuccessFeedback,
  buildAdminUserActivateConfirmDialog,
  buildAdminUserDeactivateConfirmDialog,
  buildAdminUserDeleteConfirmDialog,
  ADMIN_USER_SUCCESS_ALERT_DURATION_MS,
  type AdminUserSuccessFeedback,
  validateAdminUserFormEmail,
  hideAdminUserFromList,
  isCanonicalVaultSuperadmin,
  normalizeAdminUserForVaultUi,
  shouldAutoHideAdminUser,
  unhideAdminUserFromList,
  resolveEditableVaultTenantRole,
  roleOptionsForActor,
  tenantRoleLabel,
  userMatchesSearchTerm,
  vaultResolveAdminUsersActor,
  type VaultAdminUsersActor,
} from './admin-users.utils';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../vault-table-pagination-bind.util';
import {
  deriveSimappeLoginUsername,
  resolveAdminUserAccessUsername,
  normalizeVaultRoleToken,
  persistVaultTenantCatalog,
  VaultSessionService,
  vaultCurrentUserIdFromSession,
  vaultUserIsSuperadminFromSession,
  sessionJwtIndicatesVaultSuperadmin,
  readVaultIsSuperadminScope,
  persistVaultAuthMeTenants,
  persistVaultTenantScopeToStorage,
  persistVaultTenantSnapshot,
  persistVaultUserSwitchedTenant,
  applyVaultOperativeRoleToSession,
  VaultOperativeRoleSyncService,
  DEFAULT_ADMIN_USER_PASSWORD_POLICY,
  resolveAdminUserPasswordMinLength,
  validateAdminUserPasswordAgainstPolicy,
  doesAdminUserPasswordPolicyBlockSave,
  type AdminUserPasswordPolicy,
  VaultTokenRefreshService,
  notifyVaultAdminUsersChanged,
  readVaultItemsPerPageFromPreferences,
  VAULT_TABLE_PAGE_SIZE_OPTIONS,
  VAULT_TABLE_PAGINATION_LABELS,
} from '@nebula/shared-vault/data-access';
import { AdminUsersInputAutocompleteDirective } from './admin-users-input-autocomplete.directive';
import { AdminUserSecretFieldComponent } from './admin-user-secret-field.component';
import { AdminUserPasswordRulesComponent } from './admin-user-password-rules.component';
import { ServerContextService } from '../servers/server-context.service';
import { persistActiveTenantId } from '../../../../data-access/src/lib/shared/tenant-context.util';
import {
  bootstrapVaultAdminModuleAccess,
  createAdminModuleAccessSignal,
} from './vault-admin-module-access.util';

interface AdminUserRow {
  id: string;
  email: string;
  loginUsername: string;
  full_name: string;
  statusLabel: string;
  companySummary: string;
  roleSummary: string;
  rolesSummary: string;
  is_active: boolean;
  is_superadmin: boolean;
  isProtectedSuperadmin: boolean;
  isDeletable: boolean;
}

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    ButtonComponent,
    InputComponent,
    SelectComponent,
    CheckboxComponent,
    SearchComponent,
    TableComponent,
    EmptyComponent,
    SkeletonComponent,
    AlertComponent,
    BadgeComponent,
    FieldLabelComponent,
    GridFormComponent,
    GridFormItemComponent,
    AdminUsersInputAutocompleteDirective,
    AdminUserSecretFieldComponent,
    AdminUserPasswordRulesComponent,
    VaultPageAlertComponent,
  ],
})
export class AdminUsersComponent implements OnInit {
  protected readonly tenantRoleLabel = tenantRoleLabel;
  protected readonly successAlertDurationMs = ADMIN_USER_SUCCESS_ALERT_DURATION_MS;
  private readonly usersApi = inject(AdminUsersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tokenRefresh = inject(VaultTokenRefreshService);
  private readonly operativeRoleSync = inject(VaultOperativeRoleSyncService);
  private readonly dialogService = inject(DialogService);
  /** Marca de tiempo al abrir el flujo de alta (refresh JWT Simappe ~6 min). */
  private formCreateOpenedAtMs = 0;
  private readonly userTenantsApi = inject(AdminUserTenantsService);
  private readonly tenantsApi = inject(AdminTenantsService);
  private readonly dashboard = inject(DashboardService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly vaultSession = inject(VaultSessionService);
  private readonly serverContext = inject(ServerContextService);

  readonly canManage = createAdminModuleAccessSignal('admin_users');
  readonly actor = signal<VaultAdminUsersActor>(vaultResolveAdminUsersActor());
  readonly isSuperAdmin = computed(
    () =>
      this.actor() === 'superadmin' ||
      readVaultIsSuperadminScope() === true ||
      vaultUserIsSuperadminFromSession() ||
      sessionJwtIndicatesVaultSuperadmin(),
  );

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly successFeedback = signal<AdminUserSuccessFeedback | null>(null);
  readonly users = signal<AdminSystemUser[]>([]);
  readonly searchTerm = signal('');
  readonly tablePageSize = createVaultTablePageSizeSignal();
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];
  readonly tableSortColumn = signal<string | null>('full_name');
  readonly tableSortDirection = signal<'asc' | 'desc' | null>('asc');

  readonly allowedDomains = signal<string[]>([]);
  readonly domainRestricted = signal(false);

  readonly formOpen = signal(false);
  readonly formMode = signal<'create' | 'edit'>('create');
  readonly editingUser = signal<AdminSystemUser | null>(null);
  readonly formEmail = signal('');
  readonly formEmailError = signal('');
  readonly formUsername = signal('');
  readonly formFullName = signal('');
  readonly formPassword = signal('');
  readonly passwordPolicy = signal<AdminUserPasswordPolicy>(DEFAULT_ADMIN_USER_PASSWORD_POLICY);
  readonly formAutofillNonce = signal('');
  readonly formCreateStep = signal<1 | 2>(1);
  readonly secretFieldResetToken = signal(0);
  readonly showFormPassword = signal(false);
  readonly formActive = signal(true);
  readonly formSuperadmin = signal(false);
  readonly formCreateTenantId = signal('');
  readonly formCreateRole = signal('consulta');
  readonly formEditRole = signal('consulta');
  readonly formEditTenantId = signal('');
  readonly createFormTenants = signal<AdminTenantSummary[]>([]);

  protected currentTenantId(): string {
    return this.vaultSession.getActiveTenantContext().tenantId;
  }

  private tenantCatalogInferenceOptions() {
    const scopeTenantId = this.currentTenantId();
    return {
      scopeTenantId,
      allowScopeInference: this.actor() === 'admin_empresa' && !!scopeTenantId.trim(),
    };
  }

  readonly stats = computed(() => {
    const list = this.users();
    return {
      total: list.length,
      active: list.filter((u) => u.is_active).length,
      inactive: list.filter((u) => !u.is_active).length,
      superadmins: list.filter((u) => u.is_superadmin).length,
    };
  });

  readonly hasSuperadminInList = computed(() =>
    this.users().some((u) => u.is_superadmin),
  );

  readonly filteredUsers = computed(() =>
    this.users().filter((u) => userMatchesSearchTerm(u, this.searchTerm())),
  );

  readonly tableData = computed<AdminUserRow[]>(() =>
    this.filteredUsers().map((u) => ({
      id: u.id,
      email: u.email,
      loginUsername: resolveAdminUserAccessUsername(u),
      full_name: u.full_name,
      statusLabel: u.is_active ? 'Activo' : 'Inactivo',
      companySummary: formatAdminUserCompanySummary(u, this.createFormTenants()),
      roleSummary: formatAdminUserRoleSummary(u, this.createFormTenants()),
      rolesSummary: formatAdminUserRolesSummary(u, this.createFormTenants()),
      is_active: u.is_active,
      is_superadmin: u.is_superadmin,
      isProtectedSuperadmin: isCanonicalVaultSuperadmin(u),
      isDeletable: canDeleteAdminUser(u),
    })),
  );

  readonly tableConfig = computed<TableConfig<AdminUserRow>>(() => ({
    columns: [
      { key: 'full_name', label: 'Nombre', align: 'left', sortable: true },
      { key: 'loginUsername', label: 'Usuario', align: 'left', sortable: true },
      { key: 'email', label: 'Email', align: 'left', sortable: true },
      { key: 'companySummary', label: 'Empresa', align: 'left', sortable: true },
      { key: 'roleSummary', label: 'Rol', align: 'left', sortable: true },
      { key: 'statusLabel', label: 'Estado', align: 'center', width: '110px', cellTemplate: true },
    ],
    actions: [
      { label: 'Editar', icon: 'edit', action: 'edit' },
      {
        label: 'Desactivar',
        icon: 'LockOpen',
        action: 'toggle-active',
        color: 'warn',
        visible: (row) => row.is_active && !row.isProtectedSuperadmin,
      },
      {
        label: 'Activar',
        icon: 'Lock',
        action: 'toggle-active',
        visible: (row) => !row.is_active && !row.isProtectedSuperadmin,
      },
      {
        label: 'Eliminar',
        icon: 'delete',
        action: 'delete',
        color: 'warn',
        visible: (row) => row.isDeletable,
      },
    ],
    showPagination: true,
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
    paginationLabels: { ...VAULT_TABLE_PAGINATION_LABELS },
    initialSort: { column: 'full_name', direction: 'asc' },
    ariaLabel: 'Listado de usuarios del sistema',
    actionsLabel: 'Acciones',
    rowClickable: false,
  }));

  readonly roleOptions = computed(() => roleOptionsForActor(this.actor()));

  readonly pageSubtitle = computed(() =>
    this.isSuperAdmin()
      ? 'Administra todos los usuarios del sistema'
      : 'Administra los usuarios de tu empresa',
  );

  readonly formIntroText = computed(() =>
    this.formMode() === 'create'
      ? 'Complete los datos para registrar un nuevo usuario.'
      : 'Actualice los datos del usuario. Deje la contraseña vacía si no desea cambiarla.',
  );

  readonly formLoginUsernamePreview = computed(() => {
    const typed = this.formUsername().trim();
    if (typed) return typed;
    return deriveSimappeLoginUsername(this.formEmail());
  });

  readonly loginAccessFieldTooltip = computed(() =>
    this.formMode() === 'create'
      ? 'Usuario de acceso (login OAuth). Se completa solo con la parte del correo antes de @; no es editable.'
      : 'Usuario de acceso al sistema.',
  );

  readonly emailFieldTooltip = computed(() => {
    const domains = this.allowedDomains();
    if (this.isSuperAdmin() && this.domainRestricted() && domains.length > 0) {
      return `Dominios permitidos: ${domains.map((d) => `@${d}`).join(', ')}.`;
    }
    if (this.isSuperAdmin() && this.domainRestricted()) {
      return 'Correo corporativo autorizado.';
    }
    return 'Correo del nuevo usuario.';
  });

  readonly fullNameFieldTooltip = computed(() => 'Nombre visible en listados y reportes.');

  readonly createPasswordLength = computed(() => this.formPassword().trim().length);

  readonly createPasswordReady = computed(
    () =>
      this.createPasswordLength() > 0 &&
      !doesAdminUserPasswordPolicyBlockSave(this.passwordPolicy(), this.formPassword()),
  );

  readonly formPasswordBlocksSave = computed(() => {
    if (this.createPasswordLength() <= 0) return false;
    return doesAdminUserPasswordPolicyBlockSave(this.passwordPolicy(), this.formPassword());
  });

  readonly createPasswordFieldTooltip = computed(
    () =>
      `Clave inicial Simappe: mínimo ${resolveAdminUserPasswordMinLength(
        this.passwordPolicy(),
      )} caracteres, según la política vigente.`,
  );

  readonly passwordFieldTooltip = computed(() =>
    this.formMode() === 'create'
      ? ''
      : 'Opcional. Deje vacía para conservar la clave actual.',
  );

  readonly activeFieldTooltip = computed(() => 'Habilita o bloquea el acceso.');

  readonly superadminFieldTooltip = computed(() => {
    if (this.hasSuperadminInList() && this.formMode() === 'create') {
      return 'Ya existe un superadministrador en el sistema. Solo puede haber uno.';
    }
    return 'Acceso global a todas las empresas.';
  });

  readonly createTenantFieldTooltip = computed(() => 'Empresa asignada al usuario.');

  readonly createRoleFieldTooltip = computed(() => 'Rol operativo en Vault.');

  readonly editRoleFieldTooltip = computed(() => 'Rol operativo en la empresa.');

  readonly editCompanyFieldTooltip = computed(() =>
    this.showEditCompanyPicker()
      ? 'Empresa Vault del catálogo.'
      : 'Empresa asignada al usuario.',
  );

  readonly editingUserHasCompany = computed(() => {
    const user = this.editingUser();
    if (!user || user.is_superadmin) return false;
    const tenants = inferAdminUserTenantsFromCatalog(
      user,
      this.createFormTenants(),
      this.tenantCatalogInferenceOptions(),
    );
    return !!(tenants[0]?.tenant_id?.trim() ?? user.tenants?.[0]?.tenant_id?.trim());
  });

  readonly showEditCompanyPicker = computed(() => {
    if (this.formMode() !== 'edit' || !this.isSuperAdmin()) return false;
    const user = this.editingUser();
    if (!user || user.is_superadmin) return false;
    return true;
  });

  readonly editingUserCompanySummary = computed(() => {
    const user = this.editingUser();
    if (!user || user.is_superadmin) return '';
    const primary = resolvePrimaryAdminUserTenant(
      user.tenants ?? [],
      this.createFormTenants(),
      user.id,
    );
    if (!primary?.tenant_id) return 'Sin empresa asignada';
    return primary.tenant_name?.trim() || primary.tenant_id;
  });

  readonly canEditEditingUserRole = computed(() => {
    const user = this.editingUser();
    if (!user || user.is_superadmin || this.formMode() !== 'edit') return false;
    const tenants = inferAdminUserTenantsFromCatalog(
      user,
      this.createFormTenants(),
      this.tenantCatalogInferenceOptions(),
    );
    const assignmentRole = tenants[0]?.role ?? user.tenants?.[0]?.role ?? '';
    return canActorEditUserTenantAssignment(this.actor(), {
      targetUserId: user.id,
      currentUserId: vaultCurrentUserIdFromSession(),
      assignmentRole,
    });
  });

  readonly createFormTenantOptions = computed(() =>
    filterAssignableAdminTenants(this.createFormTenants()).map((t) => ({
      label: t.name,
      value: t.id,
    })),
  );

  ngOnInit(): void {
    bindVaultTablePageSizeToPreferences(this.tablePageSize, this.destroyRef);
    bootstrapVaultAdminModuleAccess(
      this.canManage,
      'admin_users',
      this.dashboard,
      this.cdr,
      () => {
        this.reload();
        if (this.isSuperAdmin()) this.loadAllowedDomains();
      },
      this.actor,
    );
    if (!this.canManage()) {
      this.loading.set(false);
    }
  }

  reload(options?: { silent?: boolean }): void {
    if (!this.canManage()) return;
    if (!options?.silent) {
      this.loading.set(true);
      this.clearPageFeedback();
    }
    this.usersApi
      .list({ globalScope: this.isSuperAdmin() })
      .pipe(
        switchMap((rows) => this.applyTenantCatalogToUsers(rows)),
        finalize(() => {
          if (!options?.silent) this.loading.set(false);
        }),
      )
      .subscribe({
        next: (rows) => {
          const catalog = this.createFormTenants();
          syncAdminUserAssignmentSnapshotsFromUsers(rows);
          const globalScope = this.isSuperAdmin();
          const normalized = rows.map((user) => normalizeAdminUserForVaultUi(user));
          if (globalScope) {
            for (const user of normalized) {
              unhideAdminUserFromList(user);
            }
          } else {
            for (const user of normalized) {
              if (shouldAutoHideAdminUser(user, catalog)) continue;
              unhideAdminUserFromList(user);
            }
          }
          this.users.set(
            ensureCanonicalSuperadminInAdminUserList(normalized, catalog, { globalScope }),
          );
        },
        error: (err: unknown) => {
          if (!options?.silent) {
            this.error.set(
              toModuleAccessErrorMessage(err, {
                moduleLabel: 'Gestión de Usuarios',
                fallback: 'No se pudieron cargar los usuarios.',
              }),
            );
          }
        },
      });
  }

  /** Actualiza `is_active` en memoria para que el candado cambie al instante (LockOpen ↔ Lock). */
  private patchUserActiveState(userId: string, isActive: boolean): void {
    this.users.update((list) =>
      list.map((u) => (u.id === userId ? { ...u, is_active: isActive } : u)),
    );
    this.cdr.markForCheck();
  }

  /** Conserva empresa/rol en memoria y respaldo local tras crear o asignar. */
  private rememberUserTenantAssignment(
    user: AdminSystemUser,
    params?: { tenant_id: string; role: string },
  ): void {
    const catalog = this.createFormTenants();
    const primary =
      resolvePrimaryAdminUserTenant(user.tenants ?? [], catalog, user.id) ??
      user.tenants?.[0];
    const tenantId = primary?.tenant_id?.trim() || params?.tenant_id?.trim() || '';
    const role =
      primary?.role?.trim() ||
      params?.role?.trim() ||
      resolveEditableVaultTenantRole(user.tenants) ||
      'consulta';
    if (!tenantId || user.is_superadmin) return;
    const company = catalog.find((c) => c.id === tenantId);
    const tenant_name = primary?.tenant_name?.trim() || company?.name || '';
    persistAdminUserAssignmentSnapshot(user.id, {
      tenant_id: tenantId,
      role,
      tenant_name,
    });
    this.patchUserTenants(user.id, [
      {
        id: primary?.id ?? '',
        tenant_id: tenantId,
        tenant_name,
        role,
      },
    ]);
  }

  private loadAllowedDomains(): void {
    this.usersApi.getAllowedDomains().subscribe({
      next: (res) => {
        this.allowedDomains.set(res.allowed_domains);
        this.domainRestricted.set(res.is_restricted);
      },
      error: () => {
        this.allowedDomains.set([]);
        this.domainRestricted.set(false);
      },
    });
  }

  onTableSearchChange(value: string): void {
    this.searchTerm.set(value ?? '');
  }

  private loadTenantCatalog$(): Observable<AdminTenantSummary[]> {
    const cached = this.createFormTenants();
    if (cached.length) return of(cached);
    return this.tenantsApi.list().pipe(
      map((tenants) => {
        this.createFormTenants.set(tenants);
        persistVaultTenantCatalog(tenants.map((t) => ({ id: t.id, name: t.name })));
        return tenants;
      }),
      catchError(() => of([] as AdminTenantSummary[])),
    );
  }

  onTablePageChange(ev: { pageIndex: number; pageSize: number }): void {
    if (ev.pageSize != null) {
      this.tablePageSize.set(ev.pageSize);
    }
  }

  onTableSort(ev: SortChangeEvent<AdminUserRow>): void {
    this.tableSortColumn.set(ev.column != null ? String(ev.column) : null);
    this.tableSortDirection.set(ev.direction);
  }

  onTableAction(ev: ActionEvent<AdminUserRow>): void {
    const user = this.users().find((u) => u.id === ev.data.id);
    if (!user) return;
    switch (ev.action) {
      case 'edit':
        this.openEdit(user);
        break;
      case 'toggle-active':
        this.openToggleConfirm(user);
        break;
      case 'delete':
        this.openDelete(user);
        break;
    }
  }

  private refreshFormAutofillNonce(): void {
    this.formAutofillNonce.set(
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `nonce-${Date.now()}`,
    );
  }

  onFormEmailChange(value: string): void {
    this.formEmail.set(value);
    if (this.formEmailError()) {
      this.formEmailError.set('');
    }
    if (this.formMode() === 'create') {
      this.formUsername.set(deriveSimappeLoginUsername(value));
    }
  }

  toggleFormPasswordVisibility(): void {
    this.showFormPassword.update((visible) => !visible);
  }

  continueCreateToPasswordStep(): void {
    if (!this.validateCreateDetailsStep()) return;
    this.clearSecretPasswordField();
    this.refreshFormAutofillNonce();
    this.showFormPassword.set(false);
    this.formCreateOpenedAtMs = Date.now();
    this.formCreateStep.set(2);
  }

  backCreateToDetailsStep(): void {
    this.formCreateStep.set(1);
    this.clearSecretPasswordField();
    this.error.set(null);
  }

  openCreate(): void {
    this.dismissSuccessFeedback();
    this.refreshFormAutofillNonce();
    this.formCreateOpenedAtMs = Date.now();
    this.formCreateStep.set(1);
    this.formMode.set('create');
    this.editingUser.set(null);
    this.formEmail.set('');
    this.formEmailError.set('');
    this.formUsername.set('');
    this.formFullName.set('');
    this.formPassword.set('');
    this.showFormPassword.set(false);
    this.formActive.set(true);
    this.formSuperadmin.set(false);
    this.formCreateTenantId.set('');
    this.formCreateRole.set('consulta');
    this.formEditTenantId.set('');
    this.loadTenantCatalog$().subscribe({
      next: () => {
        const assignable = filterAssignableAdminTenants(this.createFormTenants());
        if (this.isSuperAdmin() && assignable.length === 1) {
          this.formCreateTenantId.set(assignable[0].id);
        }
      },
    });
    this.loadPasswordPolicy();
    this.formOpen.set(true);
    this.reload({ silent: true });
  }

  /** Carga la política efectiva (Simappe) para validar/renderizar reglas dinámicas. */
  private loadPasswordPolicy(): void {
    this.passwordPolicy.set(DEFAULT_ADMIN_USER_PASSWORD_POLICY);
    this.usersApi
      .getPasswordPolicy()
      .pipe(catchError(() => of(DEFAULT_ADMIN_USER_PASSWORD_POLICY)))
      .subscribe((policy) => {
        this.passwordPolicy.set(policy);
        this.cdr.markForCheck();
      });
  }

  openEdit(user: AdminSystemUser): void {
    this.refreshFormAutofillNonce();
    this.formCreateStep.set(1);
    this.formMode.set('edit');
    this.editingUser.set(user);
    this.formEmailError.set('');
    this.showFormPassword.set(false);
    this.formEmail.set(user.email);
    this.formUsername.set(resolveAdminUserAccessUsername(user));
    this.formFullName.set(user.full_name);
    this.formPassword.set('');
    this.formActive.set(user.is_active);
    this.loadPasswordPolicy();
    this.formOpen.set(true);
    if (this.isSuperAdmin()) {
      this.loadTenantCatalog$().subscribe();
    }
    this.loadUserTenantsForEdit(user);
  }

  private loadUserTenantsForEdit(user: AdminSystemUser): void {
    if (user.is_superadmin) {
      this.editingUser.set(user);
      return;
    }

    this.loadTenantCatalog$()
      .pipe(
        switchMap(() => this.usersApi.get(user.id).pipe(catchError(() => of(user)))),
      )
      .subscribe((source) => {
        const catalog = this.createFormTenants();
        const coalesced = coalesceAdminUserSingleTenant(source, catalog);
        const primary = resolvePrimaryAdminUserTenant(
          coalesced.tenants ?? [],
          catalog,
          source.id,
        );
        const tenantId =
          primary?.tenant_id?.trim() || readAdminUserPrimaryTenant(source.id);
        this.editingUser.set(coalesced);
        this.formEditRole.set(resolveEditableVaultTenantRole(coalesced.tenants));
        this.formEditTenantId.set(resolveAssignableAdminUserTenantId(tenantId, catalog));
        if (tenantId) {
          const company = catalog.find((c) => c.id === tenantId);
          const role = primary?.role ?? resolveEditableVaultTenantRole(coalesced.tenants);
          persistAdminUserAssignmentSnapshot(source.id, {
            tenant_id: tenantId,
            role,
            tenant_name: primary?.tenant_name ?? company?.name,
          });
          this.patchUserTenants(source.id, [
            {
              id: primary?.id ?? '',
              tenant_id: tenantId,
              tenant_name: primary?.tenant_name ?? company?.name ?? '',
              role: primary?.role ?? resolveEditableVaultTenantRole(coalesced.tenants),
            },
          ]);
        }
      });
  }

  private patchUserTenants(userId: string, tenants: AdminSystemUser['tenants']): void {
    if (!tenants?.length) return;
    const catalog = this.createFormTenants();
    const coalesced = coalesceAdminUserSingleTenant(
      { id: userId, is_superadmin: false, tenants } as AdminSystemUser,
      catalog,
    ).tenants ?? tenants;
    this.users.update((list) =>
      list.map((u) => (u.id === userId ? mergeAdminUserTenants(u, coalesced ?? []) : u)),
    );
    this.cdr.markForCheck();
  }

  /** Si el admin cambió la empresa del usuario logueado, alinear dashboard y configuración. */
  private syncSessionIfCurrentUser(
    userId: string,
    tenantId: string,
    role: string,
    tenantName?: string,
  ): void {
    const currentId = vaultCurrentUserIdFromSession().trim();
    if (!currentId || currentId !== userId.trim() || !tenantId.trim()) return;

    persistActiveTenantId(tenantId, this.serverContext);
    persistVaultTenantScopeToStorage(tenantId);
    persistVaultUserSwitchedTenant(false);
    persistVaultTenantSnapshot({ tenant_id: tenantId, tenant_name: tenantName, role });
    applyVaultOperativeRoleToSession(role, { tenantId, tenantName });
    this.operativeRoleSync.clearSyncCache();

    if (typeof globalThis.window !== 'undefined') {
      globalThis.window.dispatchEvent(new CustomEvent('vault-session-updated'));
      notifyVaultAdminUsersChanged();
    }
  }

  private enrichUsersWithTenantCatalog(
    rows: AdminSystemUser[],
    catalog: Array<{ id: string; name: string }>,
  ): AdminSystemUser[] {
    const inferenceOpts = this.tenantCatalogInferenceOptions();
    return rows.map((user) => {
      const hydrated = mergeAdminUserTenants(
        user,
        hydrateAdminUserTenantsFromPersistedPrimary(user, catalog),
      );
      const inferred = mergeAdminUserTenants(
        hydrated,
        inferAdminUserTenantsFromCatalog(hydrated, catalog, inferenceOpts),
      );
      return coalesceAdminUserSingleTenant(inferred, catalog);
    });
  }

  /** Si el listado no trae `tenants[]`, consulta user-tenants y persiste snapshot. */
  private backfillMissingUserTenantsFromApi(
    users: AdminSystemUser[],
    catalog: Array<{ id: string; name: string }>,
  ): Observable<AdminSystemUser[]> {
    const missing = users.filter((u) => userNeedsTenantEnrichment(u));
    if (!missing.length) return of(users);

    return forkJoin(
      missing.map((user) =>
        this.userTenantsApi.listByUser(user.id).pipe(
          map((assignments) => ({
            userId: user.id,
            tenants: userTenantAssignmentsToAdminTenants(assignments),
          })),
          catchError(() => of({ userId: user.id, tenants: [] as AdminUserTenantInfo[] })),
        ),
      ),
    ).pipe(
      map((results) => {
        const byId = new Map(
          results
            .filter((r) => r.tenants.length > 0)
            .map((r) => [r.userId, r.tenants] as const),
        );
        if (!byId.size) return users;
        return users.map((user) => {
          const tenants = byId.get(user.id);
          if (!tenants?.length) return user;
          const merged = coalesceAdminUserSingleTenant(
            mergeAdminUserTenants(user, tenants),
            catalog,
          );
          const primary = resolvePrimaryAdminUserTenant(merged.tenants ?? [], catalog, user.id);
          if (primary?.tenant_id?.trim()) {
            persistAdminUserAssignmentSnapshot(user.id, {
              tenant_id: primary.tenant_id.trim(),
              role: primary.role?.trim() || 'consulta',
              tenant_name: primary.tenant_name,
            });
          }
          return merged;
        });
      }),
    );
  }

  private applyTenantCatalogToUsers(users: AdminSystemUser[]) {
    const run = (rows: AdminSystemUser[], catalog: AdminTenantSummary[]) => {
      persistVaultTenantCatalog(catalog.map((t) => ({ id: t.id, name: t.name })));
      const enriched = this.enrichUsersWithTenantCatalog(rows, catalog);
      return this.backfillMissingUserTenantsFromApi(enriched, catalog);
    };

    const catalog = this.createFormTenants();
    if (catalog.length) {
      return run(users, catalog);
    }
    return this.tenantsApi.list().pipe(
      switchMap((tenants) => {
        this.createFormTenants.set(tenants);
        return run(users, tenants);
      }),
      catchError(() => of(users)),
    );
  }

  closeForm(): void {
    this.formOpen.set(false);
    this.formCreateStep.set(1);
    this.error.set(null);
    this.formEmailError.set('');
    this.showFormPassword.set(false);
    this.clearSecretPasswordField();
  }

  /** Navega a Permisos de módulos con el usuario preseleccionado (Fase 3: sin user-tenants). */
  openModulePermissionsFromEdit(): void {
    const user = this.editingUser();
    if (!user || user.is_superadmin) return;
    this.closeForm();
    this.navigateToModulePermissions(user);
  }

  navigateToModulePermissions(user: AdminSystemUser): void {
    const catalog = this.createFormTenants();
    const tenants = inferAdminUserTenantsFromCatalog(
      user,
      catalog,
      this.tenantCatalogInferenceOptions(),
    );
    const tenantId =
      tenants[0]?.tenant_id?.trim() ?? user.tenants?.[0]?.tenant_id?.trim() ?? this.currentTenantId();
    this.goToModulePermissions(user.id, tenantId);
  }

  private goToModulePermissions(userId: string, tenantId: string): void {
    void this.router.navigate(['../module-permissions'], {
      relativeTo: this.route,
      state: {
        prefillUserId: userId,
        ...(tenantId ? { prefillTenantId: tenantId } : {}),
      },
    });
  }

  saveUser(): void {
    if (this.formMode() === 'create' && this.formCreateStep() === 1) {
      this.continueCreateToPasswordStep();
      return;
    }

    const email = this.formEmail().trim();
    const full_name = this.formFullName().trim();
    const username = this.formLoginUsernamePreview().trim();
    const passwordForApi = this.formPassword().trim();
    this.formEmailError.set('');
    this.error.set(null);

    const emailError = validateAdminUserFormEmail(email);
    if (emailError) {
      this.formEmailError.set(emailError);
      return;
    }
    if (!full_name) {
      this.error.set('El nombre para mostrar es obligatorio.');
      return;
    }
    if (this.formMode() === 'create' && !username) {
      this.error.set('El usuario de acceso es obligatorio.');
      return;
    }
    if (this.formMode() === 'create' && !passwordForApi) {
      this.error.set('La contraseña es obligatoria al crear.');
      return;
    }
    if (passwordForApi) {
      const policyError = validateAdminUserPasswordAgainstPolicy(
        this.passwordPolicy(),
        passwordForApi,
      );
      if (policyError) {
        this.error.set(policyError);
        return;
      }
    }
    this.clearSecretPasswordField();
    this.suppressBrowserCredentialPrompt();
    this.saving.set(true);
    const mode = this.formMode();
    if (mode === 'create') {
      const isSuperadminUser = this.isSuperAdmin() && this.formSuperadmin();
      let createParams: { tenant_id: string; role: string } | undefined;
      if (!isSuperadminUser) {
        const tenantId = this.isSuperAdmin()
          ? this.formCreateTenantId().trim()
          : this.currentTenantId().trim();
        if (!tenantId) {
          this.saving.set(false);
          this.error.set(
            this.isSuperAdmin()
              ? 'Seleccione una empresa para asignar el rol operativo.'
              : 'No se pudo determinar la empresa activa. Recargue la sesión e intente de nuevo.',
          );
          return;
        }
        createParams = {
          tenant_id: tenantId,
          role: this.formCreateRole().trim() || 'consulta',
        };
      }
      const payload = {
        email,
        full_name,
        username,
        password: passwordForApi,
        is_active: this.formActive(),
        is_superadmin: isSuperadminUser,
      };
      this.maybeRefreshTokenBeforeAdminMutation()
        .pipe(
          switchMap(() => this.usersApi.create(payload, createParams)),
          catchError((err: unknown) =>
            this.retryAdminMutationAfterUnauthorized(err, () =>
              this.usersApi.create(payload, createParams),
            ),
          ),
          finalize(() => this.saving.set(false)),
        )
        .subscribe({
          next: (created) => {
            if (createParams?.tenant_id?.trim()) {
              this.rememberUserTenantAssignment(created, createParams);
            }
            const accessUsername = resolveAdminUserCreateAccessUsername(created, username);
            if (!isAdminUserCreateProvisionComplete(created)) {
              this.error.set(
                'El usuario no quedó homologado en Simappe (faltan simappe_user_id o access_username). No use las credenciales aún; reintente o revise con backend.',
              );
              this.clearSecretPasswordField();
              this.closeForm();
              this.reload({ silent: true });
              notifyVaultAdminUsersChanged();
              return;
            }
            this.showSuccessFeedback(buildAdminUserCreatedSuccessFeedback(accessUsername));
            this.clearSecretPasswordField();
            this.closeForm();
            this.reload({ silent: true });
            notifyVaultAdminUsersChanged();
          },
          error: (err: unknown) => this.applyUserFormSaveError(err, 'No se pudo crear el usuario.'),
        });
    } else {
      const ed = this.editingUser();
      if (!ed) {
        this.saving.set(false);
        return;
      }
      const update: AdminUserUpdatePayload = {
        email,
        full_name,
        is_active: this.formActive(),
      };
      if (passwordForApi) {
        update.password = passwordForApi;
        update.current_password = '';
      }

      const catalog = this.createFormTenants();
      const inferred = inferAdminUserTenantsFromCatalog(
        ed,
        catalog,
        this.tenantCatalogInferenceOptions(),
      );
      const assignment =
        resolvePrimaryAdminUserTenant(inferred.length ? inferred : ed.tenants ?? [], catalog) ??
        inferred[0] ??
        ed.tenants?.[0];
      const currentTenantId = assignment?.tenant_id?.trim() ?? '';
      const nextRole = this.formEditRole().trim() || 'consulta';
      const pickerTenantId = this.formEditTenantId().trim();
      const canPickCompany = this.showEditCompanyPicker();
      if (canPickCompany && !pickerTenantId && !currentTenantId) {
        this.saving.set(false);
        this.error.set('Seleccione una empresa del catálogo (Gestión de Empresas).');
        return;
      }
      const nextTenantId = canPickCompany ? pickerTenantId || currentTenantId : currentTenantId;
      const tenantChanged = canPickCompany && !!pickerTenantId && pickerTenantId !== currentTenantId;
      const roleChangedForForm =
        normalizeVaultRoleToken(nextRole) !== normalizeVaultRoleToken(assignment?.role ?? '');
      const roleChanged = this.canEditEditingUserRole() && roleChangedForForm;
      const isCurrentUser = vaultCurrentUserIdFromSession().trim() === ed.id.trim();

      const needsVaultAssignment =
        (canPickCompany && (tenantChanged || !currentTenantId)) || (roleChanged && !!nextTenantId);
      const assigningCompany =
        canPickCompany && (tenantChanged || (!currentTenantId && !!nextTenantId));

      this.usersApi
        .update(ed.id, update)
        .pipe(
          switchMap((savedUser) => {
            if (!needsVaultAssignment || !nextTenantId) {
              return of(savedUser);
            }
            return this.persistVaultTenantAssignment({
              userId: ed.id,
              tenantId: nextTenantId,
              role: nextRole,
              linkId: assignment?.id,
              isNewAssignment: !currentTenantId,
              tenantReassign: tenantChanged,
            }).pipe(map(() => savedUser));
          }),
          finalize(() => this.saving.set(false)),
        )
        .subscribe({
          next: () => {
            const company = catalog.find((c) => c.id === nextTenantId);
            const shouldPersistAssignment =
              !!nextTenantId &&
              (needsVaultAssignment || roleChanged || (isCurrentUser && (roleChangedForForm || tenantChanged)));
            if (shouldPersistAssignment) {
              persistAdminUserAssignmentSnapshot(ed.id, {
                tenant_id: nextTenantId,
                role: nextRole,
                tenant_name: company?.name,
              });
              this.patchUserTenants(ed.id, [
                {
                  id: assignment?.id ?? '',
                  tenant_id: nextTenantId,
                  tenant_name: company?.name ?? '',
                  role: nextRole,
                },
              ]);
            }
            if (isCurrentUser && nextTenantId) {
              this.syncSessionIfCurrentUser(ed.id, nextTenantId, nextRole, company?.name);
            } else {
              notifyVaultAdminUsersChanged();
            }
            this.showSuccessFeedback(
              buildAdminUserUpdatedSuccessFeedback({ assigningCompany, roleChanged }),
            );
            this.closeForm();
            this.reload({ silent: true });
          },
          error: (err: unknown) =>
            this.applyUserFormSaveError(
              err,
              assigningCompany
                ? 'No se pudo asignar la empresa. Verifique que el API acepte POST /api/admin/user-tenants o PUT /api/admin/users con tenant_id.'
                : 'No se pudo actualizar el usuario.',
            ),
        });
    }
  }

  /**
   * Persiste empresa+rol Vault: primero POST user-tenants; si 404, PUT users?tenant_id=&role=
   * (paridad backend `admin_user_tenants` / `admin_users.update`).
   */
  private persistVaultTenantAssignment(params: {
    userId: string;
    tenantId: string;
    role: string;
    linkId?: string;
    isNewAssignment: boolean;
    tenantReassign?: boolean;
  }): Observable<AdminSystemUser> {
    const { userId, tenantId, role, linkId, isNewAssignment, tenantReassign } = params;

    const assertTenantPersisted = (
      user: AdminSystemUser,
      options?: { allowPending?: boolean },
    ): AdminSystemUser => {
      const saved = (user.tenants ?? []).some(
        (t) => (t.tenant_id || '').trim() === tenantId.trim(),
      );
      if (saved) return user;
      if (options?.allowPending) return user;
      throw new Error(
        'El servidor no guardó la empresa asignada. Despliegue POST /api/admin/user-tenants o PUT /api/admin/users con tenant_id en el backend Vault.',
      );
    };

    const viaUsersPut = (): Observable<AdminSystemUser> =>
      this.usersApi.update(userId, {}, { tenant_id: tenantId, role }).pipe(
        switchMap((user) => {
          try {
            return of(assertTenantPersisted(user, { allowPending: true }));
          } catch {
            return this.usersApi.get(userId).pipe(
              map((refreshed) => assertTenantPersisted(refreshed, { allowPending: true })),
            );
          }
        }),
      );

    const assignNew$ = this.userTenantsApi
      .assign({ user_id: userId, tenant_id: tenantId, role })
      .pipe(
        switchMap(() => this.usersApi.get(userId)),
        map((user) => assertTenantPersisted(user)),
        catchError((err: unknown) => {
          if (isUserTenantsEndpointMissing(err)) {
            return viaUsersPut();
          }
          return throwError(() => err);
        }),
      );

    if (tenantReassign) {
      return viaUsersPut();
    }

    if (isNewAssignment || !linkId?.trim()) {
      return assignNew$;
    }

    return this.userTenantsApi.updateRole(linkId, { role }).pipe(
      switchMap(() => this.usersApi.get(userId)),
      map((user) => assertTenantPersisted(user)),
      catchError((err: unknown) => {
        if (isUserTenantsEndpointMissing(err)) {
          return viaUsersPut();
        }
        return throwError(() => err);
      }),
    );
  }

  openToggleConfirm(user: AdminSystemUser): void {
    if (isCanonicalVaultSuperadmin(user)) return;
    const config = user.is_active
      ? buildAdminUserDeactivateConfirmDialog(user)
      : buildAdminUserActivateConfirmDialog(user);
    openVaultConfirmDialog(this.dialogService, config).subscribe((confirmed) => {
      if (confirmed) this.executeToggleActive(user);
    });
  }

  private executeToggleActive(user: AdminSystemUser): void {
    if (isCanonicalVaultSuperadmin(user)) return;
    const nextActive = !user.is_active;
    this.saving.set(true);
    this.error.set(null);
    this.usersApi
      .update(user.id, { is_active: nextActive })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.patchUserActiveState(user.id, nextActive);
          this.showSuccessFeedback(buildAdminUserToggleSuccessFeedback(nextActive));
          this.reload({ silent: true });
          notifyVaultAdminUsersChanged();
        },
        error: (err: unknown) => {
          this.setError(err, 'No se pudo cambiar el estado.');
        },
      });
  }

  openDelete(user: AdminSystemUser): void {
    openVaultConfirmDialog(
      this.dialogService,
      buildAdminUserDeleteConfirmDialog(user),
    ).subscribe((confirmed) => {
      if (confirmed) this.executeDeleteUser(user);
    });
  }

  private executeDeleteUser(u: AdminSystemUser): void {
    this.saving.set(true);
    this.usersApi
      .delete(u.id)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          hideAdminUserFromList(u);
          this.showSuccessFeedback(buildAdminUserDeletedSuccessFeedback());
          this.reload({ silent: true });
          notifyVaultAdminUsersChanged();
        },
        error: (err: unknown) => {
          this.applyAdminUserDeleteError(err);
        },
      });
  }

  private showSuccessFeedback(feedback: AdminUserSuccessFeedback): void {
    this.error.set(null);
    this.successFeedback.set(feedback);
  }

  dismissSuccessFeedback(): void {
    this.successFeedback.set(null);
  }

  private clearPageFeedback(): void {
    this.error.set(null);
    this.successFeedback.set(null);
  }

  onFormCreateTenantChange(v: unknown): void {
    this.formCreateTenantId.set(String(v ?? ''));
  }

  onFormCreateRoleChange(v: unknown): void {
    this.formCreateRole.set(String(v ?? 'consulta'));
  }

  onFormEditRoleChange(v: unknown): void {
    this.formEditRole.set(String(v ?? 'consulta'));
  }

  onFormEditTenantChange(v: unknown): void {
    this.formEditTenantId.set(String(v ?? ''));
  }

  private validateCreateDetailsStep(): boolean {
    this.formEmailError.set('');
    this.error.set(null);

    const emailError = validateAdminUserFormEmail(this.formEmail().trim());
    if (emailError) {
      this.formEmailError.set(emailError);
      return false;
    }
    if (!this.formFullName().trim()) {
      this.error.set('El nombre para mostrar es obligatorio.');
      return false;
    }
    const username = this.formLoginUsernamePreview().trim();
    if (!username) {
      this.error.set('El usuario de acceso es obligatorio.');
      return false;
    }
    const duplicateMessage = resolveAdminUserCreateConflictMessage(
      this.users(),
      this.formEmail().trim(),
    );
    if (duplicateMessage) {
      this.formEmailError.set(duplicateMessage);
      return false;
    }
    if (!this.formSuperadmin()) {
      const tenantId = this.isSuperAdmin()
        ? this.formCreateTenantId().trim()
        : this.currentTenantId().trim();
      if (!tenantId) {
        this.error.set(
          this.isSuperAdmin()
            ? 'Seleccione una empresa para asignar el rol operativo.'
            : 'No se pudo determinar la empresa activa. Recargue la sesión e intente de nuevo.',
        );
        return false;
      }
    }
    return true;
  }

  private applyUserFormSaveError(err: unknown, fallback: string): void {
    const resolved = resolveAdminUserFormApiError(err);
    if (resolved) {
      const emailFieldError = shouldAssignAdminUserFormEmailError(err);
      // En paso 2 el campo Correo no está visible: mostrar también el error global.
      if (emailFieldError && this.formMode() === 'create' && this.formCreateStep() === 1) {
        this.formEmailError.set(resolved);
        this.error.set(null);
      } else {
        if (emailFieldError) {
          this.formEmailError.set(resolved);
        }
        this.error.set(resolved);
      }
      return;
    }
    this.setError(err, fallback);
  }

  private applyAdminUserDeleteError(err: unknown): void {
    const resolved = resolveAdminUserDeleteApiError(err);
    this.error.set(resolved ?? 'No se pudo eliminar el usuario. Intente de nuevo o contacte al administrador.');
  }

  private maybeRefreshTokenBeforeAdminMutation(): Observable<void> {
    const staleMs = 5 * 60 * 1000;
    if (!this.formCreateOpenedAtMs || Date.now() - this.formCreateOpenedAtMs < staleMs) {
      return of(undefined);
    }
    return this.tokenRefresh.refreshToken().pipe(
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  private retryAdminMutationAfterUnauthorized<T>(
    err: unknown,
    retry: () => Observable<T>,
  ): Observable<T> {
    if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
      return throwError(() => err);
    }
    return this.tokenRefresh.refreshToken().pipe(
      switchMap(() => retry()),
      catchError(() => throwError(() => err)),
    );
  }

  private clearSecretPasswordField(): void {
    this.formPassword.set('');
    this.showFormPassword.set(false);
    this.secretFieldResetToken.update((token) => token + 1);
  }

  private suppressBrowserCredentialPrompt(): void {
    if (typeof document === 'undefined') return;
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
  }

  private setError(err: unknown, fallback: string): void {
    this.error.set(
      toModuleAccessErrorMessage(err, {
        moduleLabel: 'Gestión de Usuarios',
        fallback,
      }),
    );
  }
}
